import { Component, Input, OnInit } from '@angular/core';
import { ChartConfiguration, ChartData, TooltipItem } from 'chart.js';
import { StatisticsService } from '../service/statistics.service';
import { UmbrellaCategoryStatistic } from '../service/umbrella-category-statistic';

@Component({
  selector: 'app-umbrella-category-view',
  templateUrl: './umbrella-category-view.component.html',
  styleUrls: ['./umbrella-category-view.component.scss'],
})
export class UmbrellaCategoryViewComponent implements OnInit {
  @Input()
  reportId!: number;

  totalCount!: number;

  public barChartOptions: ChartConfiguration<'bar'>['options'] = {
    indexAxis: 'y',
    responsive: true,
    plugins: {
      title: {
        display: true,
        text: 'Number of species by Umbrella Category',
      },
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem) => this.labelCallback(tooltipItem),
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          callback: (tickValue: string | number) => {
            if (typeof tickValue === 'number' && tickValue % 1 === 0) {
              return tickValue;
            }
          },
        },
      },
    },
  };

  public barChartData!: ChartData<'bar'>;

  statisticMap = new Map<string, UmbrellaCategoryStatistic>();

  constructor(private statisticsService: StatisticsService) {}

  ngOnInit() {
    this.statisticsService
      .getUmbrellaCategory(this.reportId)
      .subscribe({
        next: (response) => {
          console.debug('UmbrellaCategoryViewComponent: Received response from API.', response);
          const safeResponse = response ?? [];
          if (safeResponse.length === 0) {
            console.warn('UmbrellaCategoryViewComponent: API returned an empty or null response.');
          }
          this.createStatisticMap(safeResponse);
          this.updateChart(safeResponse);
        },
        error: (err) => {
          console.error('UmbrellaCategoryViewComponent: Error fetching umbrella category data.', err);
        }
      });
  }

  onExport() {
    let exportUrl = '/rest/statistics/umbrellaCategory/export';
    if (this.reportId) {
      exportUrl += '?reportId=' + this.reportId;
    }
    window.location.href = exportUrl;
  }

  private createStatisticMap(statisticList: UmbrellaCategoryStatistic[]) {
    if (statisticList) {
      this.statisticMap = new Map<string, UmbrellaCategoryStatistic>();
      statisticList.forEach((statistic) => {
        this.statisticMap.set(statistic.umbrellaCategory, statistic);
      });
    }
  }

  private labelCallback(tooltipItem: TooltipItem<'bar'>): string | string[] {
    const umbrellaCategoryLabel = tooltipItem.label;
    const umbrellaCategory = this.statisticMap.get(umbrellaCategoryLabel);
    const umbrellaCategoryStatsList = umbrellaCategory?.categoryStatisticList;
    const label: string[] = [];
    umbrellaCategoryStatsList?.forEach((category) => {
      label.push(category.category + ': ' + category.count);
    });

    if (
      umbrellaCategoryStatsList !== undefined &&
      umbrellaCategoryStatsList.length > 1
    ) {
      label.push('---------');
      label.push(
        umbrellaCategory?.umbrellaCategory + ': ' + umbrellaCategory?.count
      );
    }

    return label;
  }

  private updateChart(response: UmbrellaCategoryStatistic[]) {
    const backgroundColors = [
      'rgba(255, 99, 132, 0.5)',
      'rgba(255, 159, 64, 0.5)',
      'rgba(255, 205, 86, 0.5)',
      'rgba(75, 192, 192, 0.5)',
      'rgba(54, 162, 235, 0.5)',
      'rgba(153, 102, 255, 0.5)',
      'rgba(201, 203, 207, 0.5)',
    ];

    const hoverBackgroundColors = [
      'rgba(255, 99, 132, 0.7)',
      'rgba(255, 159, 64, 0.7)',
      'rgba(255, 205, 86, 0.7)',
      'rgba(75, 192, 192, 0.7)',
      'rgba(54, 162, 235, 0.7)',
      'rgba(153, 102, 255, 0.7)',
      'rgba(201, 203, 207, 0.7)',
    ];

    const labels: string[] = [];
    const data: number[] = [];
    const bgColors: string[] = [];
    const hoverBgColors: string[] = [];

    this.totalCount = 0;
    response.forEach((nextStat, index) => {
      data.push(nextStat.count);
      labels.push(nextStat.umbrellaCategory);
      bgColors.push(backgroundColors[index % backgroundColors.length]);
      hoverBgColors.push(hoverBackgroundColors[index % hoverBackgroundColors.length]);
      this.totalCount += nextStat.count;
    });

    console.debug(`UmbrellaCategoryViewComponent: Chart updated with ${labels.length} umbrella categories, total count: ${this.totalCount}.`);

    this.barChartData = {
      labels: labels,
      datasets: [
        {
          data: data,
          backgroundColor: bgColors,
          hoverBackgroundColor: hoverBgColors,
        },
      ],
    };
  }
}
